/* <s4s-gallery> — hero + thumbnails for the selected variant only.
 * Listens to document 's4s:variant-change' { variantId } (shared with the configurator) and swaps slides
 * from the JSON map rendered by snippets/gallery-json.liquid — no network request except the images.
 * 's4s:variant-preview' { variantId } preloads that variant's first image. */
(() => {
  if (customElements.get('s4s-gallery')) return;
  const WIDTHS = [400, 600, 800, 1000, 1200, 1400];
  const sized = (src, w) => {
    const u = new URL(src, location.href);
    u.searchParams.set('width', w);
    return u.href;
  };
  const srcset = (src) => WIDTHS.map((w) => `${sized(src, w)} ${w}w`).join(', ');
  const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  class Gallery extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      try { this.map = JSON.parse(this.querySelector('[data-gallery-map]').textContent); } catch (e) { this.map = { img: {}, v: {}, colours: {} }; }
      this.hero = this.querySelector('[data-gallery-hero]');
      this.thumbs = this.querySelector('[data-gallery-thumbs]');
      this.status = this.querySelector('[data-gallery-status]');
      this.lightbox = this.querySelector('[data-lightbox]');
      // The gallery column is position:sticky (its own stacking context) — the overlay must live on <body>
      if (this.lightbox) document.body.append(this.lightbox);
      const firstImg = this.hero.querySelector('img');
      this.sizes = firstImg?.getAttribute('sizes') || '100vw';
      this.variantId = String(this.dataset.variantId);
      this.index = 0;

      this.thumbs.addEventListener('click', (e) => {
        const b = e.target.closest('[data-thumb]');
        if (b) this.show(Number(b.dataset.thumb));
      });
      this.hero.addEventListener('click', () => this.zoom());
      this.addEventListener('keydown', (e) => {
        if (!this.hero.contains(e.target) && !this.thumbs.contains(e.target)) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); this.show(this.index + 1, true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.show(this.index - 1, true); }
      });
      this.onChange = (e) => this.setVariant(e.detail && e.detail.variantId);
      this.onPreview = (e) => this.preload(e.detail && e.detail.variantId);
      document.addEventListener('s4s:variant-change', this.onChange);
      document.addEventListener('s4s:variant-preview', this.onPreview);
      this.updateCounter();
    }
    disconnectedCallback() {
      document.removeEventListener('s4s:variant-change', this.onChange);
      document.removeEventListener('s4s:variant-preview', this.onPreview);
      this._init = false;
    }

    get slides() { return (this.map.v[this.variantId] || { s: [] }).s; }
    get entry() { return this.map.v[this.variantId] || {}; }

    slideHTML(id, i) {
      if (id === 'sw') {
        const c = this.map.colours[this.entry.c] || {};
        const sw = c.swatch
          ? `<img class="gallery__swatch-img" src="${sized(c.swatch, 440)}" alt="${esc(c.name)}" width="220" height="220">`
          : `<span class="gallery__swatch-img" style="background:${esc(c.hex)}"></span>`;
        return `<span class="gallery__swatch">${sw}<span class="gallery__swatch-name">${esc(c.name)}</span><span class="gallery__swatch-note">${esc(c.note)}</span></span>`;
      }
      const m = this.map.img[id];
      if (!m) return '';
      const prio = i === 0 ? ' fetchpriority="high"' : '';
      return `<img class="gallery__img" src="${sized(m.src, 1000)}" srcset="${srcset(m.src)}" sizes="${this.sizes}" alt="${esc(m.alt)}" width="1400" height="1400" decoding="async"${prio}>`;
    }
    noteHTML() {
      const n = this.entry.note;
      if (!n) return '';
      const c = this.map.colours[this.entry.c] || {};
      return `<span class="gallery__note"><span class="gallery__note-chip" style="background:${esc(c.hex)}"></span><span>${esc(n)}</span></span>`;
    }
    show(i, focusThumb) {
      const n = this.slides.length;
      if (!n) return;
      this.index = (i + n) % n;
      const counter = this.hero.querySelector('[data-gallery-counter]');
      const tpl = document.createElement('template');
      tpl.innerHTML = this.slideHTML(this.slides[this.index], this.index) + this.noteHTML();
      this.hero.replaceChildren(...tpl.content.childNodes, counter);
      this.thumbs.querySelectorAll('[data-thumb]').forEach((b) => {
        const on = Number(b.dataset.thumb) === this.index;
        b.setAttribute('aria-current', String(on));
        if (on && focusThumb) b.focus();
      });
      this.updateCounter(true);
    }
    updateCounter(announce) {
      const n = this.slides.length || this.thumbs.children.length || 1;
      const label = `${this.index + 1} / ${n}`;
      const counter = this.hero.querySelector('[data-gallery-counter]');
      if (counter) counter.textContent = label;
      if (announce && this.status) this.status.textContent = `Image ${this.index + 1} of ${n}`;
    }
    thumbHTML(id, i) {
      let inner;
      if (id === 'sw') {
        const c = this.map.colours[this.entry.c] || {};
        inner = c.swatch ? `<img src="${sized(c.swatch, 200)}" alt="" loading="lazy">` : `<span class="gallery__thumb-chip" style="background:${esc(c.hex)}"></span>`;
      } else {
        const m = this.map.img[id];
        inner = m ? `<img src="${sized(m.src, 200)}" srcset="${sized(m.src, 100)} 100w, ${sized(m.src, 200)} 200w" sizes="100px" alt="" loading="lazy">` : '';
      }
      return `<button type="button" class="gallery__thumb" data-thumb="${i}" aria-label="Show image ${i + 1}" aria-current="${i === 0}">${inner}</button>`;
    }
    /* Old slides are replaced in the same frame the new ones are inserted: another variant's image is never shown. */
    setVariant(id) {
      id = String(id || '');
      if (!id || id === this.variantId || !this.map.v[id]) return;
      const prev = this.map.v[this.variantId];
      const next = this.map.v[id];
      this.variantId = id;
      const same = prev && JSON.stringify(prev.s) === JSON.stringify(next.s) && prev.note === next.note && prev.c === next.c;
      if (same) return;
      this.thumbs.innerHTML = this.slides.map((s, i) => this.thumbHTML(s, i)).join('');
      this.show(0);
    }
    preload(id) {
      const e = this.map.v[String(id)];
      const first = e && e.s.find((s) => s !== 'sw');
      if (!first || !this.map.img[first]) return;
      const img = new Image();
      img.sizes = this.sizes;
      img.srcset = srcset(this.map.img[first].src);
    }
    zoom() {
      if (!this.lightbox) return;
      const id = this.slides[this.index];
      const m = this.map.img[id];
      if (!m) return;
      const img = this.lightbox.querySelector('[data-lightbox-img]');
      img.src = sized(m.src, 2000);
      img.alt = m.alt || '';
      this.lightbox.hidden = false;
      window.S4S?.lock?.(true);
      this.lightbox.querySelector('[data-modal-close]')?.focus();
      this.lightbox.onclick = (e) => { if (e.target === this.lightbox || e.target === img) { this.lightbox.hidden = true; window.S4S?.lock?.(false); this.hero.focus(); } };
    }
  }
  customElements.define('s4s-gallery', Gallery);
})();
